import React, { useEffect, useState } from 'react';
import TokenService from '../../components/TokenService';
import { header } from '../../components/header.encry';
import StoresHelper from '../../components/storesHelper';
import UserService from '../../components/UserService';
import Modal from '../../components/Modal';
import { toast } from 'react-toastify';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Stack, Select, MenuItem, InputLabel, FormControl, Checkbox, ListItemText } from '@mui/material';

function TokensView() {
  const [tokens, setTokens] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ store_ids: [], user_ids: [], expires_at: '', regenerate: false, limits: [] });
  const [storesOptions, setStoresOptions] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);

  const load = async () => {
    const res = await TokenService.fetchTokens();
    setTokens(res.response || res || []);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const stores = await StoresHelper();
        setStoresOptions(stores.map(s => ({ value: s._id || s.id || s.value || s.label, label: s.label })));
      } catch (e) {
        setStoresOptions([]);
      }

      try {
        const u = await UserService.fetchUsers();
        const list = (u.response || u || []);
        setUsersOptions(list.map(user => ({ value: user._id || user.id || user, label: `${user.name || ''} ${user.lastname || ''}`.trim() })));
      } catch (e) {
        setUsersOptions([]);
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const startCreate = () => { setEditing(null); setForm({ store_ids: [], user_ids: [], expires_at: '', regenerate: false, limits: [] }); };
  const openCreate = () => { startCreate(); };

  const editToken = (t) => {
    setEditing(t._id || t.id);
    setForm({
      store_ids: t.store_ids || [],
      user_ids: t.user_ids || [],
      expires_at: t.expires_at || '',
      regenerate: false,
      limits: Array.isArray(t.limits)
        ? t.limits.map(l => (typeof l === 'string' ? { method: 'POST', path: l } : { method: (l && l.method) || 'POST', path: (l && l.path) || '' }))
        : []
    });
  };

  const addLimit = () => {
    setForm(prev => ({ ...prev, limits: [...(prev.limits || []), { method: 'POST', path: '' }] }));
  };

  const updateLimit = (index, key, value) => {
    setForm(prev => {
      const next = Array.isArray(prev.limits) ? [...prev.limits] : [];
      next[index] = { ...(next[index] || { method: 'POST', path: '' }), [key]: value };
      return { ...prev, limits: next };
    });
  };

  const removeLimit = (index) => {
    setForm(prev => {
      const next = Array.isArray(prev.limits) ? [...prev.limits] : [];
      next.splice(index, 1);
      return { ...prev, limits: next };
    });
  };

  const submit = async () => {
    try {
      const payload = { ...form };
      if (payload.expires_at) {
        // if supplied from datetime-local, convert to ISO
        const d = new Date(payload.expires_at);
        if (!Number.isNaN(d.getTime())) payload.expires_at = d.toISOString();
      }
      // normalize limits to objects { method, path }
      if (Array.isArray(payload.limits)) {
        payload.limits = payload.limits.map(l => {
          if (!l) return { method: 'POST', path: '' };
          if (typeof l === 'string') return { method: 'POST', path: l };
          return { method: l.method || 'POST', path: l.path || '' };
        });
      } else {
        payload.limits = [];
      }
      if (editing) {
        await TokenService.updateToken(editing, payload);
      } else {
        await TokenService.createToken(payload);
      }
      await load();
      setEditing(null);
      setForm({ store_ids: [], user_ids: [], expires_at: '', regenerate: false, limits: [] });
    } catch (err) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce token ?')) return;
    try {
      await TokenService.deleteToken(id);
      await load();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const copyTokenToClipboard = async (token) => {
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(String(token));
      } else {
        const ta = document.createElement('textarea');
        ta.value = String(token);
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Token copié dans le presse-papiers');
    } catch (err) {
      console.error('Copy failed', err);
      toast.error('Impossible de copier le token');
    }
  };

  return (
    <div className="container mt-4">
      <h2>Gestion des tokens</h2>
      <Stack direction="row" spacing={2} sx={{ my: 2 }}>
        <Button variant="contained" color="success" onClick={openCreate} data-bs-toggle="modal" data-bs-target="#tokenHandle">Créer un token</Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Token</TableCell>
              <TableCell>Store IDs</TableCell>
              <TableCell>User IDs</TableCell>
              <TableCell>Limits</TableCell>
              <TableCell>Expires At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tokens.map(t => (
              <TableRow key={t._id || t.id}>
                <TableCell style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 220 }}>{t.token}</span>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyTokenToClipboard(t.token)} title="Copier le token">
                      <i className="fa-regular fa-copy" />
                    </button>
                  </div>
                </TableCell>
                <TableCell>{(t.store_ids || []).map(id => storesOptions.find(s => s.value === id)?.label || id).join(', ')}</TableCell>
                <TableCell>{(t.user_ids || []).map(id => usersOptions.find(u => u.value === id)?.label || id).join(', ')}</TableCell>
                <TableCell>{(t.limits || []).slice(0,3).map(l => (typeof l === 'string' ? `POST ${l}` : `${(l && l.method) || 'POST'} ${ (l && l.path) || '' }`)).map(s => s.trim()).filter(Boolean).join(', ')}{(t.limits || []).length > 3 ? ` (+${(t.limits||[]).length - 3})` : ''}</TableCell>
                <TableCell>{t.expires_at || '—'}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => editToken(t)} data-bs-toggle="modal" data-bs-target="#tokenHandle">Modifier</Button>
                  <Button size="small" color="error" onClick={() => remove(t._id || t.id)}>Supprimer</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal modalId="tokenHandle" submitLabel={editing ? 'Mettre à jour' : 'Créer'} onSubmit={submit} title={editing ? 'Modifier token' : 'Nouveau token'}>
        <Stack spacing={2} sx={{ maxWidth: 800 }}>
          <FormControl fullWidth>
            <InputLabel id="stores-label">Magasins</InputLabel>
              <Select
              labelId="stores-label"
              multiple
              value={form.store_ids || []}
              onChange={(e) => setForm(prev => ({ ...prev, store_ids: e.target.value }))}
              renderValue={(selected) => (Array.isArray(selected) ? selected.map(v => storesOptions.find(s => s.value === v)?.label || v).join(', ') : selected)}
            >
              {storesOptions.map(s => (
                <MenuItem key={s.value} value={s.value}>
                  <Checkbox checked={(form.store_ids || []).indexOf(s.value) > -1} />
                  <ListItemText primary={s.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="users-label">Utilisateurs</InputLabel>
            <Select
              labelId="users-label"
              multiple
              value={form.user_ids || []}
              onChange={(e) => setForm(prev => ({ ...prev, user_ids: e.target.value }))}
              renderValue={(selected) => selected.map(v => usersOptions.find(u=>u.value===v)?.label || v).join(', ')}
            >
              {usersOptions.map(u => (
                <MenuItem key={u.value} value={u.value}>
                  <Checkbox checked={(form.user_ids || []).indexOf(u.value) > -1} />
                  <ListItemText primary={u.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <div>
            <label className="form-label">Limits</label>
            {(form.limits || []).map((lim, idx) => (
              <div key={idx} className="d-flex gap-2 mb-2">
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel id={`method-${idx}`}>Méthode</InputLabel>
                  <Select
                    labelId={`method-${idx}`}
                    value={(lim && lim.method) || 'POST'}
                    label="Méthode"
                    onChange={(e) => updateLimit(idx, 'method', e.target.value)}
                    size="small"
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="PATCH">PATCH</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                  </Select>
                </FormControl>
                <input
                  type="text"
                  className="form-control"
                  placeholder="/endpoint/path"
                  value={(lim && lim.path) || ''}
                  onChange={(e) => updateLimit(idx, 'path', e.target.value)}
                />
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeLimit(idx)}>Supprimer</button>
              </div>
            ))}
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={addLimit}>Ajouter une limite</button>
          </div>

          <TextField
            label="Date d'expiration"
            type="datetime-local"
            value={form.expires_at || ''}
            onChange={(e) => setForm(prev => ({ ...prev, expires_at: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Modal>
    </div>
  );
}

export default TokensView;
